import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * A subscriber's recharge receipts, and sending ONE to that subscriber --
 * radaccess /access/operator/{ns}/subscribers/{AccountID}/receipts (owner
 * 2026-10-10: a receipt for every payment, sent only to a subscriber who asks;
 * from no-reply@ankhos.io on the operator's behalf). RBAC resource
 * `subscriberreceipt` (superadmin and Operator).
 *
 * The receipt is the recharge's own number, RCPT/<FY>/<id>. radcomm reads the
 * receipt and the subscriber's verified address itself; the console only names
 * which receipt.
 */
export interface SubscriberReceipt {
  receiptno: string;
  /** As written by the database, already IST ("2026-08-13 09:55"). Never converted. */
  time: string;
  amount: number;
  plan: string;
  method: string;
  /** Reversed, or itself a reversal: cannot be sent. */
  reversed: boolean;
  /** The items the receipt is for ("Plan ₹449 (uses 1 pin)"); absent from a radaccess before v1.112. */
  note?: string;
  /** "other": a sale's separate receipt for non-internet items (owner 2026-10-15/17). */
  kind?: "internet" | "other";
}

export interface SubscriberReceipts {
  receipts: SubscriberReceipt[];
  /** "a***@example.com", or "" when no address is recorded. */
  emailmasked: string;
  emailverified: boolean;
}

const base = (ns: string, accountId: string) =>
  `/operator/${encodeURIComponent(ns)}/subscribers/${encodeURIComponent(accountId)}/receipts`;

export async function getSubscriberReceipts(identity: CallerIdentity, ns: string, accountId: string, signal?: AbortSignal): Promise<SubscriberReceipts> {
  const r = await callGateway<SubscriberReceipts | null>(identity, config().gatewayBase, base(ns, accountId), signal ? { signal } : {});
  return {
    receipts: r.data?.receipts ?? [],
    emailmasked: r.data?.emailmasked ?? "",
    emailverified: Boolean(r.data?.emailverified),
  };
}

export async function sendSubscriberReceipt(identity: CallerIdentity, ns: string, accountId: string, receiptno: string): Promise<{ receiptno: string; to: string }> {
  const r = await callGateway<{ receiptno: string; to: string }>(identity, config().gatewayBase, `${base(ns, accountId)}/send`, {
    method: "POST",
    body: { receiptno },
  });
  return r.data;
}

/**
 * Why a receipt cannot be sent, or "" when it can. The address rule is the
 * server's (radcomm refuses an unverified address); this only says so before
 * the click instead of after.
 */
export function whyNotSendable(r: SubscriberReceipt, list: Pick<SubscriberReceipts, "emailmasked" | "emailverified">): string {
  if (r.reversed) return "Reversed — cannot be sent";
  if (!list.emailmasked) return "No email address recorded for this subscriber";
  if (!list.emailverified) return "The subscriber's email address is not verified";
  return "";
}
